/**
 * 外部客户端（如 WorkBuddy）内嵌打开 BRM 页面时的入口参数解析。
 *
 * 目前支持一个 URL 参数：
 *  - `openFrom=workbuddy`：标记页面由 WorkBuddy 客户端打开。该标记会影响两处行为：
 *    缺少登录态时走 WorkBuddy 授权链路；请求头与 cookie 的 `os` 统一上报为 `workbuddy`。
 *
 * 客户端通常只在首个链接上带参数，站内跳转后 query 就丢了，
 * 因此解析结果按标签页会话粘滞保存，整个会话内维持同一来源；
 * 需要中途清除时显式传 `openFrom=` 覆盖。
 *
 * 没有复用更通用的 `from`，是因为站内已有 `from=mcp` 等业务取值，
 * 复用会在跳转到这些页面时把粘滞的来源冲掉。
 */
import { cookie } from '@/utils/storageOperateApi';

/** URL 参数名：打开本页的外部客户端 */
const OPEN_FROM_QUERY_KEY = 'openFrom';
/** `openFrom` 取值：WorkBuddy 客户端 */
export const OPEN_FROM_WORKBUDDY = 'workbuddy';

/** sessionStorage key：粘滞的来源客户端 */
const OPEN_FROM_SESSION_KEY = 'EMBED_OPEN_FROM';

/** WorkBuddy 首页所属应用，跨 BRM / Portal 跳转时使用稳定枚举而非外部传入域名 */
export const WORKBUDDY_HOME_APP = Object.freeze({
  BRM: 'brm',
  PORTAL: 'portal'
});

/** WorkBuddy 入口与跨项目跳转统一透传的首页参数 */
export const WORKBUDDY_HOME_QUERY_KEYS = Object.freeze({
  app: 'workBuddyHomeApp',
  path: 'workBuddyHomePath'
});

const WORKBUDDY_HOME_APP_SESSION_KEY = 'WORKBUDDY_HOME_APP';
const WORKBUDDY_HOME_PATH_SESSION_KEY = 'WORKBUDDY_HOME_PATH';
const WORKBUDDY_HOME_APPS = new Set(Object.values(WORKBUDDY_HOME_APP));
const WORKBUDDY_HOME_PATH_MAX_LENGTH = 4096;
const WORKBUDDY_HOME_PLACEHOLDER_ORIGIN = 'https://workbuddy-home.invalid';
const WORKBUDDY_HOME_CONTROL_QUERY_KEYS = [
  OPEN_FROM_QUERY_KEY,
  WORKBUDDY_HOME_QUERY_KEYS.app,
  WORKBUDDY_HOME_QUERY_KEYS.path,
  'historyFrom'
];
const WORKBUDDY_HOME_SENSITIVE_QUERY_KEYS = ['code', 'token', 'webtoken', 'userInfo'];

/** 同页判定白名单：命中路由仅按 path 判断是否停留在首页，query 变化不视为换页 */
export const WORKBUDDY_SAME_PAGE_ROUTE_NAMES = Object.freeze([
  // 市场活动：路演 / 调研 / 策略会共用 /activity/roadshow，仅靠 ?tab 区分 Tab 页
  'ActivityRoadshow',
  // 自选股：/market/self-selected 页内切换股票仅改变 query
  'stockDetail'
]);

/** 请求头与 cookie 中标识 WorkBuddy 客户端的 os 取值，是与后端约定的独立契约，不随 openFrom 取值联动 */
const WORKBUDDY_OS = 'workbuddy';
/** cookie 名：与请求头 os 同名，供后端在拿不到自定义请求头的场景识别来源 */
const OS_COOKIE_KEY = 'os';

/**
 * 读取 sessionStorage，隐私模式等禁用场景静默降级为空值。
 * @param {string} key - 存储键
 * @returns {string} 已保存的值，不可用时返回空串
 */
const readSession = (key) => {
  try {
    return sessionStorage.getItem(key) || '';
  } catch {
    return '';
  }
};

/**
 * 写入 sessionStorage，不可用时忽略，参数退化为仅本次导航生效。
 * @param {string} key - 存储键
 * @param {string} value - 待保存的值
 * @returns {void}
 */
const writeSession = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* sessionStorage 不可用时不做粘滞 */
  }
};

/** 删除会话级存储，存储不可用时静默降级。 */
const removeSession = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* sessionStorage 不可用时无需额外处理 */
  }
};

/**
 * 判断 query 上是否显式带了某个参数，用于区分「没传」与「传了空值」。
 * @param {object} query - 路由 query 对象
 * @param {string} key - 参数名
 * @returns {boolean} 是否显式传参
 */
const hasQueryKey = (query, key) => Object.prototype.hasOwnProperty.call(query || {}, key);

/**
 * 取出 query 上的单个字符串值，兼容同名参数被解析成数组的情况。
 * @param {object} query - 路由 query 对象
 * @param {string} key - 参数名
 * @returns {string} 去除首尾空格后的参数值
 */
const readQueryValue = (query, key) => {
  const value = query?.[key];
  // 同名参数重复出现时 vue-router 会给出数组，取第一个即可
  const single = Array.isArray(value) ? value[0] : value;
  // `?openFrom` 这种无值写法会被解析成 null，统一按空串处理
  return String(single ?? '').trim();
};

/**
 * 校验并清洗 WorkBuddy 首页相对路径。
 * 只允许当前两个受控前端内的站内路径，同时移除来源、历史与登录凭证参数。
 * @param {unknown} rawPath - URL query 中传入的首页路径
 * @returns {string} 可安全使用的相对路径，非法时返回空串
 */
const normalizeWorkBuddyHomePath = (rawPath) => {
  const path = String(rawPath ?? '').trim();
  if (!path || path.length > WORKBUDDY_HOME_PATH_MAX_LENGTH || !path.startsWith('/') || path.startsWith('//')) {
    return '';
  }
  if (path.includes('\\')) return '';

  try {
    const url = new URL(path, WORKBUDDY_HOME_PLACEHOLDER_ORIGIN);
    if (url.origin !== WORKBUDDY_HOME_PLACEHOLDER_ORIGIN) return '';

    [...WORKBUDDY_HOME_CONTROL_QUERY_KEYS, ...WORKBUDDY_HOME_SENSITIVE_QUERY_KEYS].forEach((key) => {
      url.searchParams.delete(key);
    });
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return '';
  }
};

/** 清理当前 origin 下保存的 WorkBuddy 首页。 */
export const clearWorkBuddyHome = () => {
  removeSession(WORKBUDDY_HOME_APP_SESSION_KEY);
  removeSession(WORKBUDDY_HOME_PATH_SESSION_KEY);
};

/**
 * 读取当前 origin 下的 WorkBuddy 首页目标。
 * @returns {{app: string, path: string}|null} 合法目标，无记录时返回 null
 */
export const getWorkBuddyHomeTarget = () => {
  const app = readSession(WORKBUDDY_HOME_APP_SESSION_KEY);
  const path = normalizeWorkBuddyHomePath(readSession(WORKBUDDY_HOME_PATH_SESSION_KEY));
  if (!WORKBUDDY_HOME_APPS.has(app) || !path) return null;
  return { app, path };
};

/**
 * 将入口或跨项目 URL 上的 WorkBuddy 首页同步到当前 origin 会话。
 * 非 WorkBuddy 环境下即使伪造首页参数也不会生效。
 * @param {object} query - 当前路由 query
 * @param {boolean} isWorkBuddy - 已由来源协议确认的 WorkBuddy 会话状态
 * @param {{app?: string, path?: string}} fallbackTarget - 未显式传首页参数时，以首次 WorkBuddy 入口作为首页
 * @returns {boolean} 首页存储是否发生变化
 */
export const syncWorkBuddyHome = (query = {}, isWorkBuddy = false, fallbackTarget = {}) => {
  const hasExplicitOpenFrom = hasQueryKey(query, OPEN_FROM_QUERY_KEY);
  if (!isWorkBuddy) {
    if (hasExplicitOpenFrom && getWorkBuddyHomeTarget()) {
      clearWorkBuddyHome();
      return true;
    }
    return false;
  }

  const hasHomeApp = hasQueryKey(query, WORKBUDDY_HOME_QUERY_KEYS.app);
  const hasHomePath = hasQueryKey(query, WORKBUDDY_HOME_QUERY_KEYS.path);
  if (!hasHomeApp && !hasHomePath) {
    if (!hasExplicitOpenFrom || getWorkBuddyHomeTarget()) return false;

    const fallbackApp = String(fallbackTarget.app ?? '')
      .trim()
      .toLowerCase();
    const fallbackPath = normalizeWorkBuddyHomePath(fallbackTarget.path);
    if (!WORKBUDDY_HOME_APPS.has(fallbackApp) || !fallbackPath) return false;

    writeSession(WORKBUDDY_HOME_APP_SESSION_KEY, fallbackApp);
    writeSession(WORKBUDDY_HOME_PATH_SESSION_KEY, fallbackPath);
    return true;
  }

  const app = readQueryValue(query, WORKBUDDY_HOME_QUERY_KEYS.app).toLowerCase();
  const path = normalizeWorkBuddyHomePath(readQueryValue(query, WORKBUDDY_HOME_QUERY_KEYS.path));
  const previous = getWorkBuddyHomeTarget();

  if (!WORKBUDDY_HOME_APPS.has(app) || !path) {
    if (previous) clearWorkBuddyHome();
    return Boolean(previous);
  }

  if (previous?.app === app && previous.path === path) return false;
  writeSession(WORKBUDDY_HOME_APP_SESSION_KEY, app);
  writeSession(WORKBUDDY_HOME_PATH_SESSION_KEY, path);
  return true;
};

/**
 * 让 os cookie 与当前识别到的来源保持一致。
 *
 * 写成会话级 cookie，与 sessionStorage 的粘滞范围对齐，浏览器关闭即失效；
 * cookie 无法按标签页隔离，因此非 WorkBuddy 会话要主动清掉其它标签页残留的标记。
 * 取值没变化时不写，避免每次构造请求头都操作 document.cookie。
 * @param {boolean} isWorkBuddy - 当前是否处于 WorkBuddy 客户端会话
 * @returns {void}
 */
const syncOsCookie = (isWorkBuddy) => {
  const current = cookie.get(OS_COOKIE_KEY);
  if (isWorkBuddy) {
    if (current !== WORKBUDDY_OS) cookie.set(OS_COOKIE_KEY, WORKBUDDY_OS, { expires: 0 });
    return;
  }
  if (current === WORKBUDDY_OS) cookie.remove(OS_COOKIE_KEY);
};

/**
 * 当前页面是否由 WorkBuddy 客户端打开。
 * @param {object} query - 路由 query 对象
 * @returns {boolean} 是否来自 WorkBuddy 客户端
 * @example
 * // /res/summary/list?openFrom=workbuddy
 * isOpenedFromWorkBuddy(route.query); // true
 */
export const isOpenedFromWorkBuddy = (query) => {
  // 显式传参时以本次 URL 为准并粘滞到整个会话，站内跳转丢掉参数后沿用会话记录
  let isWorkBuddy;
  if (hasQueryKey(query, OPEN_FROM_QUERY_KEY)) {
    const source = readQueryValue(query, OPEN_FROM_QUERY_KEY).toLowerCase();
    writeSession(OPEN_FROM_SESSION_KEY, source);
    isWorkBuddy = source === OPEN_FROM_WORKBUDDY;
  } else {
    isWorkBuddy = readSession(OPEN_FROM_SESSION_KEY) === OPEN_FROM_WORKBUDDY;
  }
  syncOsCookie(isWorkBuddy);
  return isWorkBuddy;
};

/**
 * 读取地址栏 query，供拿不到路由对象的调用方（如请求头构造）使用。
 * @returns {object} query 键值对，解析失败时返回空对象
 */
const readLocationQuery = () => {
  try {
    return Object.fromEntries(new URLSearchParams(window.location.search).entries());
  } catch {
    return {};
  }
};

/**
 * 当前标签页是否处于 WorkBuddy 客户端会话，基于地址栏与会话粘滞判断。
 * @returns {boolean} 是否来自 WorkBuddy 客户端
 */
export const isWorkBuddyClient = () => isOpenedFromWorkBuddy(readLocationQuery());

/**
 * 读取可透传给另一个前端的 WorkBuddy 首页 query。
 * 只有当前仍是 WorkBuddy 会话时才返回参数，避免普通环境带出残留存储。
 * @returns {object} 可直接展开到目标 query 的参数
 */
export const getWorkBuddyHomeQuery = () => {
  if (!isWorkBuddyClient()) return {};
  const target = getWorkBuddyHomeTarget();
  if (!target) return {};
  return {
    [WORKBUDDY_HOME_QUERY_KEYS.app]: target.app,
    [WORKBUDDY_HOME_QUERY_KEYS.path]: target.path
  };
};

/**
 * 按应用到 origin 的受控映射生成 WorkBuddy 首页绝对地址。
 * @param {Record<string, string>} appOrigins - brm / portal 到当前环境 origin 的映射
 * @returns {string} 可跳转的首页地址，无效时返回空串
 */
export const resolveWorkBuddyHomeUrl = (appOrigins = {}) => {
  if (!isWorkBuddyClient()) return '';
  const target = getWorkBuddyHomeTarget();
  const configuredOrigin = target ? appOrigins[target.app] : '';
  if (!target || !configuredOrigin) return '';

  try {
    const originUrl = new URL(configuredOrigin, window.location.origin);
    const homeUrl = new URL(target.path, originUrl.origin);
    if (homeUrl.origin !== originUrl.origin) return '';

    homeUrl.searchParams.set(OPEN_FROM_QUERY_KEY, OPEN_FROM_WORKBUDDY);
    homeUrl.searchParams.set(WORKBUDDY_HOME_QUERY_KEYS.app, target.app);
    homeUrl.searchParams.set(WORKBUDDY_HOME_QUERY_KEYS.path, target.path);
    return homeUrl.href;
  } catch {
    return '';
  }
};

/**
 * 取 fullPath 的 path 部分，供同页白名单忽略 query 与 hash 后比较。
 * @param {string} fullPath - 路由 fullPath
 * @returns {string} 不含 query 与 hash 的路径
 */
const extractRoutePath = (fullPath) => String(fullPath ?? '').split(/[?#]/)[0];

/**
 * 判断当前路由是否已是 WorkBuddy 首页，比较时忽略来源与跨项目控制参数。
 * 白名单内的路由（如路演/调研共用页）只按 path 比较，切换 Tab 等 query 变化仍视为停留在首页。
 * @param {string} currentApp - 当前项目应用枚举
 * @param {{name?: string, fullPath?: string}} route - 当前路由对象
 * @returns {boolean} 是否已处于首页
 */
export const isCurrentWorkBuddyHome = (currentApp, route) => {
  const target = getWorkBuddyHomeTarget();
  if (!target || target.app !== currentApp) return false;
  if (WORKBUDDY_SAME_PAGE_ROUTE_NAMES.includes(route?.name)) {
    return extractRoutePath(route?.fullPath) === extractRoutePath(target.path);
  }
  return normalizeWorkBuddyHomePath(route?.fullPath) === target.path;
};

/**
 * 计算请求头 os 的取值。
 * @param {string} defaultOs - 非 WorkBuddy 场景下沿用的原有取值
 * @returns {string} WorkBuddy 会话返回 `workbuddy`，否则返回 `defaultOs`
 * @example
 * resolveOsHeader(isElectron ? 'pc-app' : 'brm'); // 'workbuddy'
 */
export const resolveOsHeader = (defaultOs) => (isWorkBuddyClient() ? WORKBUDDY_OS : defaultOs);
